/** Independent Polyquant rows for main and Russian views. This subscribes to the existing service;
 * it does not start a provider poller or combine odds across sources. */
import { alignFork } from '../lib/align';
import { isPolyquantId } from '../lib/polyquant';
import type { Fork } from '../lib/protocol';
import { FeedClient } from '../ws/client';

let client: FeedClient | null = null;
let forks = new Map<string, Fork>();
let generation = 0;

export const polyquantSnapshot = (): Map<string, Fork> => forks;

export function startPolyquantFeed(publish: () => void): void {
  if (client !== null) return;
  const gen = ++generation;
  let received = false;
  const current = (): boolean => gen === generation;
  const clear = (): void => {
    if (!current() || forks.size === 0) return;
    forks = new Map();
    publish();
  };
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  client = new FeedClient(`${scheme}://${location.host}/feed6-api/ws`, 3, {
    onState: (incoming) => {
      if (!current()) return;
      received = true;
      forks = new Map([...incoming].filter(([id]) => isPolyquantId(id)));
      publish();
    },
    onStatus: (status) => {
      if (status !== 'live') {
        if (status !== 'connecting') received = true;
        clear();
      }
    },
    onProtocolMismatch: () => { received = true; clear(); },
    onSeatRevoked: () => { received = true; clear(); },
  });
  client.connect();
  // Bootstrap once. A late REST response must never resurrect a deleted row,
  // overwrite an empty socket frame, or cross a tab switch.
  void (async () => {
    try {
      const response = await fetch('/feed6-api/v1/signals', {
        credentials: 'same-origin', signal: AbortSignal.timeout(4000),
      });
      if (!response.ok) return;
      const body = await response.json() as { forks?: Fork[]; proto?: number };
      if (!current() || received || body.proto !== 3 || !Array.isArray(body.forks)) return;
      const now = Date.now();
      forks = new Map(body.forks.filter(f => isPolyquantId(f.id)).map(f => [f.id, { ...alignFork(f), at: now }]));
      publish();
    } catch { /* Other sources continue to display. */ }
  })();
}

export function stopPolyquantFeed(): void {
  generation += 1;
  client?.close();
  client = null;
  forks = new Map();
}
